import { useState, useRef, useMemo, useEffect } from 'react'
import { useSearchParams, useLocation } from 'react-router-dom'
import { useTheme, alpha, lighten, darken, styled } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import EmailIcon from '@mui/icons-material/Email'
import EditNoteIcon from '@mui/icons-material/EditNote'
import SaveAsIcon from '@mui/icons-material/SaveAs'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import FilterListOffIcon from '@mui/icons-material/FilterListOff'
import {
  DataGridPremium,
  DataGridPremiumProps,
  GridPaginationModel,
  useGridApiContext,
  gridClasses,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
  GridToolbarProps,
  ToolbarPropsOverrides,
  GridFilterModel,
  GridSortModel,
  GridValidRowModel,
  GridRowId,
} from '@mui/x-data-grid-premium'
import { frFR } from '@mui/x-data-grid-premium/locales'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { SystemStyleObject } from '@mui/system'
import { Theme } from '@mui/material/styles'
import Cookies from 'js-cookie'

import { DEFAULT_PAGE_SIZE } from '../../../services/events'

const ODD_OPACITY = 0.5

const StyledStripedDataGrid = styled(DataGridPremium)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    '&:hover': {
      backgroundColor: alpha(darken(theme.palette.grey[200], 0.1), ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        darken(theme.palette.grey[200], 0.1),
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      '&:hover': {
        backgroundColor: alpha(
          darken(theme.palette.grey[200], 0.1),
          ODD_OPACITY + theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity
        ),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(
            darken(theme.palette.grey[200], 0.1),
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
  // Warning
  [`& .${gridClasses.row}.warning`]: {
    backgroundColor: lighten(theme.palette.warning.light, 0.75),
    '&:hover': {
      backgroundColor: alpha(lighten(theme.palette.warning.light, 0.1), ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        lighten(theme.palette.warning.light, 0.1),
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      '&:hover': {
        backgroundColor: alpha(
          lighten(theme.palette.warning.light, 0.1),
          ODD_OPACITY + theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity
        ),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(
            lighten(theme.palette.warning.light, 0.1),
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
}))

declare module '@mui/x-data-grid-premium' {
  interface ToolbarPropsOverrides {
    emailOptions?: {
      subject: string
      body: string
      getValue: (row: GridValidRowModel) => string
    }
    saveAllOptions?: {
      onClick: () => void
    }
    editOptions?: {
      onClick: (rows: Map<GridRowId, GridValidRowModel>) => void
    }
    hideExport?: boolean
  }
}

type CustomToolbarProps = GridToolbarProps & ToolbarPropsOverrides

const responsiveBtnDataGridStyles = {
  '> .MuiButton-icon, > .MuiTouchRipple-root': {
    maxWidth: '2.5rem',
    minWidth: '2.5rem',
  },
  maxWidth: '2.5rem',
  minWidth: '2.5rem',
  overflow: 'hidden',
  justifyContent: 'left',
}

function CustomToolbar({
  emailOptions,
  editOptions,
  saveAllOptions,
  hideExport,
  showQuickFilter,
}: CustomToolbarProps) {
  const { t } = useTranslation()
  const apiRef = useGridApiContext()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))

  const [hasFilter, setFilterState] = useState<boolean>(false)

  useEffect(() => {
    if (apiRef.current == null) {
      return
    }
    apiRef.current.subscribeEvent('filterModelChange', event => {
      if (!event.items.length && !event.quickFilterValues?.length) {
        setFilterState(false)
      } else {
        setFilterState(true)
      }
    })
  }, [apiRef])

  return (
    <GridToolbarContainer sx={{ paddingBottom: '0.5rem ' }}>
      <Tooltip title={t('Réinitialiser les filtres')}>
        <IconButton
          onClick={() => {
            apiRef.current?.setFilterModel({ items: [] }, 'removeAllFilterItems')
            apiRef.current?.setQuickFilterValues([])
          }}
          disabled={!hasFilter}
          sx={!hasFilter ? { opacity: 0, display: isMobile ? 'none' : undefined } : undefined}
        >
          <FilterListOffIcon />
        </IconButton>
      </Tooltip>
      <GridToolbarFilterButton
        slotProps={{
          button: { variant: 'text', sx: isMobile ? responsiveBtnDataGridStyles : undefined },
        }}
      />
      <GridToolbarColumnsButton
        slotProps={{
          button: { variant: 'text', sx: isMobile ? responsiveBtnDataGridStyles : undefined },
        }}
      />
      <GridToolbarDensitySelector
        slotProps={{
          button: { variant: 'text', sx: isMobile ? responsiveBtnDataGridStyles : undefined },
        }}
      />
      <Box sx={{ flexGrow: 1 }} />
      {showQuickFilter && (
        <GridToolbarQuickFilter
          slotProps={{
            input: { sx: isMobile ? { width: '10rem' } : undefined },
          }}
        />
      )}
      {saveAllOptions && (
        <Tooltip title={t('Enregistrer tout')}>
          <Button startIcon={<SaveAsIcon />} variant="text" onClick={saveAllOptions.onClick}>
            {t('Enregistrer')}
          </Button>
        </Tooltip>
      )}
      {editOptions && (
        <Tooltip title={t('Édition groupée')}>
          <Button
            startIcon={<EditNoteIcon />}
            variant="text"
            onClick={() => {
              const selectedRows = apiRef.current?.getSelectedRows()
              if (!selectedRows?.size) {
                toast.info(t('Veuillez sélectionner au moins une ligne'))
                return
              }
              editOptions.onClick(selectedRows)
            }}
          >
            {t('Éditer')}
          </Button>
        </Tooltip>
      )}
      {emailOptions && (
        <Tooltip title={t('Envoyer un email groupé')}>
          <Button
            startIcon={<EmailIcon />}
            variant="text"
            onClick={() => {
              const selectedRows = apiRef.current?.getSelectedRows()
              if (!selectedRows?.size) {
                toast.info(t('Veuillez sélectionner au moins une ligne'))
                return
              }

              const emails: string[] = []
              selectedRows?.forEach(value => {
                const email = emailOptions.getValue(value)
                emails.push(email)
              })

              window.open(
                `mailto:?subject=${emailOptions.subject}&body=${emailOptions.body}&bcc=${emails.join(',')}`,
                '_blank'
              )
            }}
            sx={isMobile ? responsiveBtnDataGridStyles : undefined}
          >
            {t('Email')}
          </Button>
        </Tooltip>
      )}
      {!hideExport && (
        <GridToolbarExport
          slotProps={{
            button: { variant: 'text', sx: isMobile ? responsiveBtnDataGridStyles : undefined },
          }}
        />
      )}
    </GridToolbarContainer>
  )
}

export default function StripedDataGrid({
  count,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  emailOptions,
  editOptions,
  saveAllOptions,
  pagination = true,
  paginationMode = 'server',
  hideExport,
  getRowClassName = () => '',
  ...props
}: DataGridPremiumProps &
  CustomToolbarProps & { count?: number; page?: number; pageSize?: number }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: searchParams.get('page') ? Number(searchParams.get('page')) - 1 : page - 1,
    pageSize: searchParams.get('pageSize') ? Number(searchParams.get('pageSize')) : pageSize,
  })

  const filterModel = !!Cookies.get(`filterModel_${location.pathname}`)
    ? (JSON.parse(Cookies.get(`filterModel_${location.pathname}`)!) as GridFilterModel)
    : props.initialState?.filter?.filterModel
  const sortModel = !!Cookies.get(`sortModel_${location.pathname}`)
    ? (JSON.parse(Cookies.get(`sortModel_${location.pathname}`)!) as GridSortModel)
    : props.initialState?.sorting?.sortModel

  useEffect(() => {
    const newPage = String(paginationModel.page + 1)
    const newPageSize = String(paginationModel.pageSize)
    if (newPage !== searchParams.get('page') || newPageSize !== searchParams.get('pageSize')) {
      searchParams.set('page', newPage)
      searchParams.set('pageSize', newPageSize)
      setSearchParams(searchParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize])

  // Some API clients return undefined while loading
  // Following lines are here to prevent `count` from being undefined during the loading
  const rowCountRef = useRef(count ?? 0)
  const rowCount = useMemo(() => {
    if (count !== undefined) {
      rowCountRef.current = count
    }
    return rowCountRef.current
  }, [count])

  return (
    <StyledStripedDataGrid
      pageSizeOptions={[5, 10, 25, 50, 100, 200]}
      pagination={pagination}
      getRowClassName={params =>
        getRowClassName(params) || (params.indexRelativeToCurrentPage % 2 === 0 ? 'odd' : 'even')
      }
      localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      paginationMode={paginationMode}
      rowCount={paginationMode !== 'client' ? rowCount : undefined}
      checkboxSelection
      ignoreDiacritics
      {...props}
      sx={theme => {
        const baseStyles: SystemStyleObject<Theme> = {
          boxShadow: 2,
          border: 2,
          borderColor: 'divider',
        }
        const additionalStyles: SystemStyleObject<Theme> =
          typeof props.sx === 'function'
            ? props.sx(theme)
            : ((props.sx as SystemStyleObject<Theme>) ?? {})
        return { ...baseStyles, ...additionalStyles }
      }}
      slots={{
        toolbar: CustomToolbar,
        ...props.slots,
      }}
      slotProps={{
        ...props.slotProps,
        toolbar: {
          emailOptions,
          editOptions,
          hideExport,
          saveAllOptions,
          showQuickFilter: true,
          ...props.slotProps?.toolbar,
        },
      }}
      initialState={{
        ...props.initialState,
        filter: {
          ...props.initialState?.filter,
          filterModel,
        },
        sorting: {
          ...props.initialState?.sorting,
          sortModel,
        },
      }}
      onFilterModelChange={(model, ...args) => {
        const params = JSON.stringify(model)
        Cookies.set(`filterModel_${location.pathname}`, params)

        props.onFilterModelChange?.(model, ...args)
      }}
      onSortModelChange={(model, ...args) => {
        const params = JSON.stringify(model)
        Cookies.set(`sortModel_${location.pathname}`, params)

        props.onSortModelChange?.(model, ...args)
      }}
    />
  )
}
